import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  createPettyCashCount, 
  usePettyCash, 
  getPettyCashHistory, 
  generateAuthCode, 
  getCurrentAuthCode,
  createPettyCashRequest,
  getPettyCashRequests,
  confirmPettyCashRequest,
  completePettyCashRequest,
  cancelPettyCashRequest,
  getDashboard,
  transferPettyCashToCashFlow,
  refundPettyCash
} from '../api/api';
import { Wallet, Key, AlertCircle, Send, Check, X, Clock, ArrowUpRight, ArrowDownLeft, RefreshCw } from 'lucide-react';

const PettyCash = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [currentCode, setCurrentCode] = useState(null);
  const [isGlobalCode, setIsGlobalCode] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCountForm, setShowCountForm] = useState(false);
  const [showUseForm, setShowUseForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [countData, setCountData] = useState({ 
    count_amount: '', 
    shift_number: 1,
    denominations: {
      q_050: '',
      q_1: '',
      q_5: '',
      q_10: '',
      q_20: '',
      q_50: '',
      q_100: '',
      q_200: ''
    }
  });
  const [requestData, setRequestData] = useState({
    message: '',
    denominations: {
      q_050: '',
      q_1: '',
      q_5: '',
      q_10: '',
      q_20: '',
      q_50: '',
      q_100: '',
      q_200: ''
    }
  });
  const [useData, setUseData] = useState({ amount: '', reason: '', authorization_code: '' });
  const [transferData, setTransferData] = useState({ amount: '', reason: '', authorization_code: '' });
  const [refundData, setRefundData] = useState({ amount: '', reason: '', authorization_code: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('count'); // 'count' | 'requests'

  const loadData = useCallback(async () => {
    try {
      const historyData = await getPettyCashHistory();
      setHistory(historyData);
      
      const requestsData = await getPettyCashRequests();
      setRequests(requestsData);
      
      // Obtener información de la tienda (incluye fondo asignado)
      const dashboardData = await getDashboard();
      if (dashboardData.stores && dashboardData.stores.length > 0) {
        setStoreInfo(dashboardData.stores[0]);
      }
      
      // Supervisores, managers, store_managers y admin pueden ver/generar códigos
      if (['store_manager', 'admin', 'manager', 'supervisor'].includes(user?.role)) {
        try {
          const codeData = await getCurrentAuthCode();
          setCurrentCode(codeData.code || null);
          setIsGlobalCode(codeData.is_global || false);
        } catch (e) {
          // No code available
        }
      }
    } catch (error) {
      console.error('Error loading petty cash data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calcular total de denominaciones
  const calculateDenominationsTotal = () => {
    const d = countData.denominations;
    return (
      (parseFloat(d.q_050) || 0) +
      (parseFloat(d.q_1) || 0) +
      (parseFloat(d.q_5) || 0) +
      (parseFloat(d.q_10) || 0) +
      (parseFloat(d.q_20) || 0) +
      (parseFloat(d.q_50) || 0) +
      (parseFloat(d.q_100) || 0) +
      (parseFloat(d.q_200) || 0)
    );
  };

  // Calcular total de denominaciones para solicitud
  const calculateRequestDenominationsTotal = () => {
    const d = requestData.denominations;
    return (
      (parseFloat(d.q_050) || 0) +
      (parseFloat(d.q_1) || 0) +
      (parseFloat(d.q_5) || 0) +
      (parseFloat(d.q_10) || 0) +
      (parseFloat(d.q_20) || 0) +
      (parseFloat(d.q_50) || 0) +
      (parseFloat(d.q_100) || 0) +
      (parseFloat(d.q_200) || 0)
    );
  };

  const handleDenominationChange = (field, value) => {
    setCountData({
      ...countData,
      denominations: {
        ...countData.denominations,
        [field]: value
      }
    });
  };

  const handleRequestDenominationChange = (field, value) => {
    setRequestData({
      ...requestData,
      denominations: {
        ...requestData.denominations,
        [field]: value
      }
    });
  };

  const handleCountSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    const denominationsTotal = calculateDenominationsTotal();
    const countAmount = parseFloat(countData.count_amount);

    // Validar que las denominaciones cuadren (si se ingresaron)
    if (denominationsTotal > 0 && Math.abs(denominationsTotal - countAmount) > 0.01) {
      setError(`El total de denominaciones (Q${denominationsTotal.toFixed(2)}) no cuadra con el monto contado (Q${countAmount.toFixed(2)})`);
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        count_amount: countAmount,
        shift_number: parseInt(countData.shift_number)
      };

      // Solo enviar denominaciones si se ingresaron
      if (denominationsTotal > 0) {
        payload.denominations = {
          q_050: parseFloat(countData.denominations.q_050) || 0,
          q_1: parseFloat(countData.denominations.q_1) || 0,
          q_5: parseFloat(countData.denominations.q_5) || 0,
          q_10: parseFloat(countData.denominations.q_10) || 0,
          q_20: parseFloat(countData.denominations.q_20) || 0,
          q_50: parseFloat(countData.denominations.q_50) || 0,
          q_100: parseFloat(countData.denominations.q_100) || 0,
          q_200: parseFloat(countData.denominations.q_200) || 0
        };
      }

      await createPettyCashCount(payload);
      setSuccess('¡Conteo de fondo registrado exitosamente!');
      setCountData({ 
        count_amount: '', 
        shift_number: 1,
        denominations: { q_050: '', q_1: '', q_5: '', q_10: '', q_20: '', q_50: '', q_100: '', q_200: '' }
      });
      setShowCountForm(false);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al registrar el conteo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      await usePettyCash({
        amount: parseFloat(useData.amount),
        reason: useData.reason,
        authorization_code: useData.authorization_code
      });
      setSuccess('¡Uso de fondo registrado exitosamente!');
      setUseData({ amount: '', reason: '', authorization_code: '' });
      setShowUseForm(false);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al usar el fondo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const requestTotal = calculateRequestDenominationsTotal();
      const payload = {
        message: requestData.message
      };

      // Solo enviar denominaciones si se ingresaron
      if (requestTotal > 0) {
        payload.denominations = {
          q_050: parseFloat(requestData.denominations.q_050) || 0,
          q_1: parseFloat(requestData.denominations.q_1) || 0,
          q_5: parseFloat(requestData.denominations.q_5) || 0,
          q_10: parseFloat(requestData.denominations.q_10) || 0,
          q_20: parseFloat(requestData.denominations.q_20) || 0,
          q_50: parseFloat(requestData.denominations.q_50) || 0,
          q_100: parseFloat(requestData.denominations.q_100) || 0,
          q_200: parseFloat(requestData.denominations.q_200) || 0
        };
      }

      await createPettyCashRequest(payload);
      setSuccess('¡Solicitud de sencillo enviada! Se ha notificado a los administradores.');
      setRequestData({
        message: '',
        denominations: { q_050: '', q_1: '', q_5: '', q_10: '', q_20: '', q_50: '', q_100: '', q_200: '' }
      });
      setShowRequestForm(false);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmRequest = async (requestId) => {
    try {
      await confirmPettyCashRequest(requestId);
      setSuccess('¡Confirmado! Se ha notificado a la tienda que el sencillo va en camino.');
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al confirmar');
    }
  };

  const handleCompleteRequest = async (requestId) => {
    try {
      await completePettyCashRequest(requestId);
      setSuccess('Solicitud marcada como completada.');
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al completar');
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('¿Está seguro de cancelar esta solicitud?')) return;
    try {
      await cancelPettyCashRequest(requestId);
      setSuccess('Solicitud cancelada.');
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleGenerateCode = async () => {
    try {
      const shiftNumber = parseInt(prompt('Ingrese el número de turno (1, 2, o 3):'));
      if (!shiftNumber || shiftNumber < 1 || shiftNumber > 3) {
        alert('Número de turno inválido');
        return;
      }
      
      const codeData = await generateAuthCode(shiftNumber);
      setCurrentCode(codeData.code);
      setIsGlobalCode(codeData.is_global || false);
      const globalMsg = codeData.is_global ? ' (GLOBAL - válido para cualquier tienda)' : '';
      setSuccess(`Código generado: ${codeData.code}${globalMsg}`);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al generar el código');
    }
  };

  // Transferir sencillo a flujo de caja
  const handleTransferToCash = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const result = await transferPettyCashToCashFlow({
        amount: parseFloat(transferData.amount),
        reason: transferData.reason,
        authorization_code: transferData.authorization_code
      });
      setSuccess(result.message);
      setTransferData({ amount: '', reason: '', authorization_code: '' });
      setShowTransferForm(false);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al transferir fondos');
    } finally {
      setSubmitting(false);
    }
  };

  // Reponer sencillo desde flujo de caja
  const handleRefundPettyCash = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const result = await refundPettyCash({
        amount: parseFloat(refundData.amount),
        reason: refundData.reason,
        authorization_code: refundData.authorization_code
      });
      setSuccess(result.message);
      setRefundData({ amount: '', reason: '', authorization_code: '' });
      setShowRefundForm(false);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al reponer fondos');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      utcDateString = dateString + 'Z';
    }
    const date = new Date(utcDateString);
    return date.toLocaleString('es-GT', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Roles que pueden generar códigos de autorización (actualizado según backend)
  const canGenerateCode = ['admin', 'manager', 'supervisor', 'store_manager'].includes(user?.role);
  // Roles que pueden confirmar solicitudes de sencillo
  const canConfirmRequests = ['admin', 'manager', 'supervisor', 'store_manager'].includes(user?.role);
  // Roles que pueden contar dinero (cajeros, encargados de tienda, y admin - NO supervisores ni managers)
  const canCountMoney = ['cashier', 'store_manager', 'admin'].includes(user?.role);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs flex items-center"><Clock size={12} className="mr-1" /> Pendiente</span>;
      case 'confirmed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs flex items-center"><Check size={12} className="mr-1" /> En camino</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Completado</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">Cancelado</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">{status}</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-gray-900" data-testid="petty-cash-title">Control de Fondo de Sencillo</h1>
        <div className="flex flex-wrap gap-2">
          {canGenerateCode && (
            <button
              onClick={handleGenerateCode}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              data-testid="generate-code-button"
            >
              <Key size={20} />
              <span>Generar Código</span>
            </button>
          )}
          {canCountMoney && (
            <button
              onClick={() => { setShowCountForm(!showCountForm); setShowUseForm(false); setShowRequestForm(false); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              data-testid="count-button"
            >
              <Wallet size={20} />
              <span>Contar Fondo</span>
            </button>
          )}
          {canCountMoney && (
            <button
              onClick={() => { setShowUseForm(!showUseForm); setShowCountForm(false); setShowRequestForm(false); }}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              data-testid="use-button"
            >
              Usar Fondo
            </button>
          )}
          <button
            onClick={() => { setShowRequestForm(!showRequestForm); setShowCountForm(false); setShowUseForm(false); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            data-testid="request-button"
          >
            <Send size={20} />
            <span>Solicitar Sencillo</span>
          </button>
        </div>
      </div>

      {canGenerateCode && currentCode && (
        <div className={`${isGlobalCode ? 'bg-green-50 border-green-200' : 'bg-purple-50 border-purple-200'} border rounded-lg p-4`}>
          <div className="flex items-center space-x-3">
            <Key className={isGlobalCode ? 'text-green-600' : 'text-purple-600'} size={24} />
            <div>
              <p className={`text-sm font-medium ${isGlobalCode ? 'text-green-800' : 'text-purple-800'}`}>
                Código de Autorización {isGlobalCode ? 'GLOBAL' : 'Actual'}
              </p>
              <p className={`text-2xl font-bold tracking-wider ${isGlobalCode ? 'text-green-900' : 'text-purple-900'}`} data-testid="current-auth-code">{currentCode}</p>
              <p className={`text-xs mt-1 ${isGlobalCode ? 'text-green-600' : 'text-purple-600'}`}>
                {isGlobalCode ? '🌐 Válido para CUALQUIER tienda - 8 horas' : 'Válido por 8 horas'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info del Fondo Asignado */}
      {storeInfo && storeInfo.petty_cash_assigned > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-800 font-medium">Fondo de Sencillo Asignado</p>
              <p className="text-2xl font-bold text-blue-900">{storeInfo.currency}{storeInfo.petty_cash_assigned.toFixed(2)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-blue-800 font-medium">Saldo Actual</p>
              <p className={`text-2xl font-bold ${storeInfo.petty_cash_fund === storeInfo.petty_cash_assigned ? 'text-green-600' : 'text-red-600'}`}>
                {storeInfo.currency}{storeInfo.petty_cash_fund.toFixed(2)}
              </p>
            </div>
          </div>
          {storeInfo.petty_cash_fund !== storeInfo.petty_cash_assigned && (
            <p className="text-xs text-red-600 mt-2">
              ⚠️ El conteo debe cuadrar con el fondo asignado
            </p>
          )}
          
          {/* Botones de Transferencia y Reposición */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-blue-200">
            <button
              onClick={() => setShowTransferForm(!showTransferForm)}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
              title="Mover dinero del sencillo al flujo de caja"
            >
              <ArrowUpRight size={18} />
              <span>Sencillo → Caja</span>
            </button>
            <button
              onClick={() => setShowRefundForm(!showRefundForm)}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm"
              title="Reponer el fondo de sencillo desde el flujo de caja"
            >
              <ArrowDownLeft size={18} />
              <span>Caja → Sencillo</span>
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2" data-testid="petty-cash-error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700"><X size={18} /></button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between" data-testid="petty-cash-success">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700"><X size={18} /></button>
        </div>
      )}

      {/* Formulario de Transferir Sencillo a Caja */}
      {showTransferForm && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-orange-800 mb-2">Transferir Sencillo a Flujo de Caja</h2>
          <p className="text-orange-700 text-sm mb-4">
            Use esta opción cuando necesite hacer un pago pero no tiene suficiente efectivo en caja.
            El dinero saldrá del fondo de sencillo y entrará al flujo de caja disponible.
          </p>
          <form onSubmit={handleTransferToCash} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto a Transferir</label>
                <input
                  type="number"
                  step="0.01"
                  value={transferData.amount}
                  onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Razón</label>
                <input
                  type="text"
                  value={transferData.reason}
                  onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ej: Pago a proveedor"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código de Autorización</label>
                <input
                  type="text"
                  value={transferData.authorization_code}
                  onChange={(e) => setTransferData({ ...transferData, authorization_code: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="ABC123"
                  required
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {submitting ? 'Procesando...' : 'Transferir a Caja'}
              </button>
              <button
                type="button"
                onClick={() => setShowTransferForm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulario de Reponer Sencillo */}
      {showRefundForm && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-teal-800 mb-2">Reponer Fondo de Sencillo</h2>
          <p className="text-teal-700 text-sm mb-4">
            Use esta opción para devolver dinero al fondo de sencillo después de haberlo usado.
            El dinero saldrá del flujo de caja y regresará al fondo de sencillo.
          </p>
          <form onSubmit={handleRefundPettyCash} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto a Reponer</label>
                <input
                  type="number"
                  step="0.01"
                  value={refundData.amount}
                  onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Razón</label>
                <input
                  type="text"
                  value={refundData.reason}
                  onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ej: Reposición semanal"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código de Autorización</label>
                <input
                  type="text"
                  value={refundData.authorization_code}
                  onChange={(e) => setRefundData({ ...refundData, authorization_code: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="ABC123"
                  required
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {submitting ? 'Procesando...' : 'Reponer Sencillo'}
              </button>
              <button
                type="button"
                onClick={() => setShowRefundForm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulario de Solicitud de Sencillo */}
      {showRequestForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Solicitar Sencillo</h2>
          <p className="text-gray-600 text-sm mb-4">
            Se enviará una notificación a los administradores, supervisores y encargados para que lleven sencillo a su tienda.
          </p>
          <form onSubmit={handleRequestSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensaje / Detalle de lo que necesita
              </label>
              <textarea
                value={requestData.message}
                onChange={(e) => setRequestData({ ...requestData, message: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ej: Necesito sencillo para dar cambio a clientes"
                rows={2}
                required
              />
            </div>

            {/* Denominaciones solicitadas */}
            <div className="border-t pt-4">
              <h3 className="text-md font-medium text-gray-700 mb-3">¿Qué denominaciones necesita? (Opcional)</h3>
              <p className="text-xs text-gray-500 mb-3">Ingrese el monto total que necesita de cada denominación</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q0.50</label>
                  <input
                    type="number"
                    step="0.50"
                    value={requestData.denominations.q_050}
                    onChange={(e) => handleRequestDenominationChange('q_050', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q1.00</label>
                  <input
                    type="number"
                    step="1"
                    value={requestData.denominations.q_1}
                    onChange={(e) => handleRequestDenominationChange('q_1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q5.00</label>
                  <input
                    type="number"
                    step="5"
                    value={requestData.denominations.q_5}
                    onChange={(e) => handleRequestDenominationChange('q_5', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q10.00</label>
                  <input
                    type="number"
                    step="10"
                    value={requestData.denominations.q_10}
                    onChange={(e) => handleRequestDenominationChange('q_10', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q20.00</label>
                  <input
                    type="number"
                    step="20"
                    value={requestData.denominations.q_20}
                    onChange={(e) => handleRequestDenominationChange('q_20', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q50.00</label>
                  <input
                    type="number"
                    step="50"
                    value={requestData.denominations.q_50}
                    onChange={(e) => handleRequestDenominationChange('q_50', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q100.00</label>
                  <input
                    type="number"
                    step="100"
                    value={requestData.denominations.q_100}
                    onChange={(e) => handleRequestDenominationChange('q_100', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q200.00</label>
                  <input
                    type="number"
                    step="200"
                    value={requestData.denominations.q_200}
                    onChange={(e) => handleRequestDenominationChange('q_200', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="mt-3 text-right">
                <span className="text-sm text-gray-600">Total Solicitado: </span>
                <span className="font-bold text-blue-600">
                  Q{calculateRequestDenominationsTotal().toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {submitting ? 'Enviando...' : 'Enviar Solicitud'}
              </button>
              <button
                type="button"
                onClick={() => setShowRequestForm(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulario de Conteo con Denominaciones */}
      {showCountForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Contar Fondo de Sencillo</h2>
          <form onSubmit={handleCountSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto Total Contado
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={countData.count_amount}
                  onChange={(e) => setCountData({ ...countData, count_amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                  data-testid="count-amount-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Turno
                </label>
                <select
                  value={countData.shift_number}
                  onChange={(e) => setCountData({ ...countData, shift_number: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="shift-number-select"
                >
                  <option value="1">Turno 1 (5:00am - 1:00pm)</option>
                  <option value="2">Turno 2 (1:00pm - 9:00pm)</option>
                  <option value="3">Turno 3 (9:00pm - 5:00am)</option>
                </select>
              </div>
            </div>

            {/* Denominaciones */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-md font-medium text-gray-700 mb-3">Desglose por Denominaciones (Opcional)</h3>
              <p className="text-xs text-gray-500 mb-3">El total de las denominaciones debe cuadrar con el monto contado</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q0.50</label>
                  <input
                    type="number"
                    step="0.50"
                    value={countData.denominations.q_050}
                    onChange={(e) => handleDenominationChange('q_050', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q1.00</label>
                  <input
                    type="number"
                    step="1"
                    value={countData.denominations.q_1}
                    onChange={(e) => handleDenominationChange('q_1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q5.00</label>
                  <input
                    type="number"
                    step="5"
                    value={countData.denominations.q_5}
                    onChange={(e) => handleDenominationChange('q_5', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q10.00</label>
                  <input
                    type="number"
                    step="10"
                    value={countData.denominations.q_10}
                    onChange={(e) => handleDenominationChange('q_10', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q20.00</label>
                  <input
                    type="number"
                    step="20"
                    value={countData.denominations.q_20}
                    onChange={(e) => handleDenominationChange('q_20', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q50.00</label>
                  <input
                    type="number"
                    step="50"
                    value={countData.denominations.q_50}
                    onChange={(e) => handleDenominationChange('q_50', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q100.00</label>
                  <input
                    type="number"
                    step="100"
                    value={countData.denominations.q_100}
                    onChange={(e) => handleDenominationChange('q_100', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Q200.00</label>
                  <input
                    type="number"
                    step="200"
                    value={countData.denominations.q_200}
                    onChange={(e) => handleDenominationChange('q_200', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="mt-3 text-right">
                <span className="text-sm text-gray-600">Total Denominaciones: </span>
                <span className={`font-bold ${Math.abs(calculateDenominationsTotal() - (parseFloat(countData.count_amount) || 0)) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                  Q{calculateDenominationsTotal().toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                data-testid="submit-count-button"
              >
                {submitting ? 'Registrando...' : 'Registrar Conteo'}
              </button>
              <button
                type="button"
                onClick={() => setShowCountForm(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulario de Uso */}
      {showUseForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Usar Fondo de Sencillo</h2>
          <form onSubmit={handleUseSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monto</label>
                <input
                  type="number"
                  step="0.01"
                  value={useData.amount}
                  onChange={(e) => setUseData({ ...useData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                  data-testid="use-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Razón</label>
                <input
                  type="text"
                  value={useData.reason}
                  onChange={(e) => setUseData({ ...useData, reason: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Razón del uso"
                  required
                  data-testid="use-reason-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Código de Autorización</label>
                <input
                  type="text"
                  value={useData.authorization_code}
                  onChange={(e) => setUseData({ ...useData, authorization_code: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Código"
                  required
                  data-testid="use-auth-code-input"
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400"
                data-testid="submit-use-button"
              >
                {submitting ? 'Procesando...' : 'Usar Fondo'}
              </button>
              <button
                type="button"
                onClick={() => setShowUseForm(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabs para Historial y Solicitudes */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="border-b">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('count')}
              className={`px-6 py-3 font-medium text-sm ${activeTab === 'count' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Historial de Conteos
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-6 py-3 font-medium text-sm ${activeTab === 'requests' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Solicitudes de Sencillo {requests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {requests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
          </nav>
        </div>
        
        {activeTab === 'count' && (
          <>
            {loading ? (
              <div className="p-8 text-center text-gray-600">Cargando...</div>
            ) : history.length === 0 ? (
              <div className="p-8 text-center text-gray-600">No hay conteos registrados</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha/Hora</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Turno</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contado Por</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {history.map((count) => (
                      <tr key={count.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(count.timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          Turno {count.shift_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                          Q{count.count_amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {count.counted_by_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'requests' && (
          <>
            {requests.length === 0 ? (
              <div className="p-8 text-center text-gray-600">No hay solicitudes de sencillo</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tienda</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Solicitado Por</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mensaje</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map((req) => (
                      <tr key={req.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(req.timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {req.store_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {req.requested_by_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                          {req.message}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(req.status)}
                          {req.confirmed_by_name && (
                            <p className="text-xs text-gray-500 mt-1">Por: {req.confirmed_by_name}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                          {req.status === 'pending' && canConfirmRequests && (
                            <button
                              onClick={() => handleConfirmRequest(req.id)}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                              title="Confirmar que llevará sencillo"
                            >
                              Confirmar
                            </button>
                          )}
                          {req.status === 'confirmed' && canConfirmRequests && (
                            <button
                              onClick={() => handleCompleteRequest(req.id)}
                              className="text-green-600 hover:text-green-800 font-medium"
                              title="Marcar como completado"
                            >
                              Completar
                            </button>
                          )}
                          {(req.status === 'pending' || req.status === 'confirmed') && (
                            <button
                              onClick={() => handleCancelRequest(req.id)}
                              className="text-red-600 hover:text-red-800 font-medium"
                              title="Cancelar solicitud"
                            >
                              Cancelar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PettyCash;
